// components/PaymentSection.jsx
import React from "react";
import { Form, Field } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import { useLocalization } from "../../../../context/LocalizeContextProvider";
import { useAuth } from "../../../../context/AuthProvider";
import AsyncSelect from "../../AsyncSelect";
import SelectField from "../../SelectField";
import InputField from "../../InputField";
import Button from "../../Button";
import { formatNumberWithCurrency } from "../../../../utils/utils";
import {
  paymentMethod,
  typeDiscount,
  roleUser,
} from "../../../../utils/constants";
import styles from "../ModalDetailReservationV2.module.css";
import { useModal } from "../../../../context/ModalProvider";

export const TablePaymentItem = ({
  paymentData,
  fields = [],
  itemOptions = [],
  packageOptions = [],
  disabledForm = false,
  handleChangeItem,
  handleRemoveItem,
  handleUndoItem,
  isDoctor = false,
  isParent = false,
}) => {
  const localize = useLocalization();
  const modal = useModal();

  const requiredField = (value, label) => {
    if (!value) {
      return localize.getText("generalRequiredFieldValidation", {
        fieldName: label,
      });
    }
    return undefined;
  };

  const handlePackageUndo = (id, type) => {
    if (type === "package") {
      modal.showConfirmation({
        title: localize.getText("areYouSure"),
        message: localize.getText("undoActivedPackage"),
        onConfirm: () => handleUndoItem(id, type),
      });
    } else if (id) {
      modal.showConfirmation({
        title: localize.getText("areYouSure"),
        message: localize.getText("undoActivedDeposit"),
        onConfirm: () => handleUndoItem(id, type),
      });
    }
  };

  return (
    <div className={styles.paymentTable}>
      <div className={styles.tableHeader}>
        <p className={`${styles.tableHeaderItem} ${styles.tableHeaderName}`}>
          {localize.getText("name")}
        </p>
        <p
          className={`${styles.tableHeaderItem} ${styles.tableHeaderQuantity}`}
        >
          {localize.getText("quantity")}
        </p>
        <p className={`${styles.tableHeaderItem} ${styles.tableHeaderPrice}`}>
          {localize.getText("price")}
        </p>
        {!isDoctor && (
          <>
            <p
              className={`${styles.tableHeaderItem} ${styles.tableHeaderDiscount}`}
            >
              {localize.getText("discountType")}
            </p>
            <p
              className={`${styles.tableHeaderItem} ${styles.tableHeaderDiscountValue}`}
            >
              {localize.getText("discount")}
            </p>
          </>
        )}
        <p style={{ width: 48 }}></p>
      </div>

      {fields.map((name, index) => {
        const item = fields.value[index];

        return (
          <div key={index} className={styles.tableRow}>
            <div className={styles.tableHeaderName}>
              {item?.isPackage ? (
                <Field
                  name={
                    isParent
                      ? `paymentItemsParent[${index}].package`
                      : `${name}.package`
                  }
                  validate={(value) =>
                    requiredField(value, localize.getText("package"))
                  }
                >
                  {({ input, meta }) => (
                    <AsyncSelect
                      placeholder={localize.getText("choosePackage")}
                      size="small"
                      fullWidth
                      error={meta.touched && meta.error}
                      value={paymentData.paymentItems[index]?.package}
                      options={packageOptions}
                      labelKey="name"
                      valueKey="id"
                      disabled={
                        disabledForm ||
                        (item?.isPackage && item?.amount === 0) ||
                        (item?.isDeposit && item?.amount === 0)
                      }
                      onChange={(value) => {
                        input.onChange(value);
                        if (!isParent) {
                          handleChangeItem(index, "package", value);
                        }
                      }}
                    />
                  )}
                </Field>
              ) : (
                <Field
                  name={
                    isParent
                      ? `paymentItemsParent[${index}].item`
                      : `${name}.item`
                  }
                  validate={(value) =>
                    requiredField(value, localize.getText("item"))
                  }
                >
                  {({ input, meta }) => (
                    <AsyncSelect
                      placeholder={localize.getText("chooseItem")}
                      size="small"
                      fullWidth
                      isGroup
                      error={meta.touched && meta.error}
                      value={paymentData.paymentItems[index]?.item}
                      options={itemOptions}
                      labelKey="label"
                      valueKey="value"
                      disabled={
                        disabledForm || (item?.isDeposit && item?.amount === 0)
                      }
                      onChange={(value) => {
                        input.onChange(value);
                        if (!isParent) {
                          handleChangeItem(index, "item", value);
                        }
                      }}
                    />
                  )}
                </Field>
              )}
            </div>

            <Field
              name={
                isParent ? `paymentItemsParent[${index}].qty` : `${name}.qty`
              }
              validate={(value) =>
                requiredField(value, localize.getText("quantity"))
              }
            >
              {({ input, meta }) => (
                <InputField
                  placeholder="0"
                  type="number"
                  size="small"
                  classNameInput={styles.tableHeaderQuantity}
                  error={meta.touched && meta.error}
                  disabled={disabledForm || item?.isPackage || item?.isDeposit}
                  value={paymentData.paymentItems[index]?.qty}
                  onChange={(value) => {
                    input.onChange(value);
                    if (!isParent) {
                      handleChangeItem(index, "qty", value);
                    }
                  }}
                />
              )}
            </Field>

            <Field
              name={
                isParent
                  ? `paymentItemsParent[${index}].amount`
                  : `${name}.amount`
              }
              validate={(value) =>
                !item?.isPackage && !item?.isDeposit
                  ? requiredField(value, localize.getText("price"))
                  : undefined
              }
            >
              {({ input, meta }) => (
                <InputField
                  placeholder="0"
                  type="number"
                  size="small"
                  classNameInput={styles.tableHeaderPrice}
                  error={meta.touched && meta.error}
                  disabled={disabledForm || item?.isPackage || item?.isDeposit}
                  value={paymentData.paymentItems[index]?.amount}
                  onChange={(value) => {
                    input.onChange(value);
                    if (!isParent) {
                      handleChangeItem(index, "amount", value);
                    }
                  }}
                />
              )}
            </Field>

            {!isDoctor && (
              <>
                <Field
                  name={
                    isParent
                      ? `paymentItemsParent[${index}].typeDiscount`
                      : `${name}.typeDiscount`
                  }
                >
                  {({ input, meta }) => (
                    <SelectField
                      data={typeDiscount}
                      labelKey="label"
                      valueKey="value"
                      size="small"
                      className={styles.tableHeaderDiscount}
                      placeholder={localize.getText("selectDiscountType")}
                      error={meta.touched && meta.error}
                      value={paymentData.paymentItems[index]?.typeDiscount}
                      disabled={
                        disabledForm ||
                        (item?.isPackage && item?.amount === 0) ||
                        (item?.isDeposit && item?.amount === 0)
                      }
                      onChange={(value) => {
                        input.onChange(value);
                        if (!isParent) {
                          handleChangeItem(index, "typeDiscount", value);
                        }
                      }}
                    />
                  )}
                </Field>

                <Field
                  name={
                    isParent
                      ? `paymentItemsParent[${index}].discount`
                      : `${name}.discount`
                  }
                >
                  {({ input, meta }) => (
                    <InputField
                      placeholder="0"
                      type="number"
                      size="small"
                      classNameInput={styles.tableHeaderDiscountValue}
                      error={meta.touched && meta.error}
                      value={paymentData.paymentItems[index]?.discount}
                      disabled={!item?.typeDiscount || disabledForm}
                      onChange={(value) => {
                        input.onChange(value);
                        if (!isParent) {
                          handleChangeItem(index, "discount", value);
                        }
                      }}
                    />
                  )}
                </Field>
              </>
            )}

            {/* Only show delete button if not parent and more than 1 item */}
            {item.isRedo && item?.additionalId ? (
              <Button
                onClick={() =>
                  handlePackageUndo(
                    item?.additionalId,
                    item?.isPackage ? "package" : "deposit"
                  )
                }
                tooltip={
                  item?.isPackage
                    ? localize.getText("cancelActivePackage")
                    : localize.getText("cancelActiveDeposit")
                }
                variant="info"
                size="sm"
              >
                <i className="fas fa-undo" />
              </Button>
            ) : (
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRemoveItem(index)}
                disabled={
                  disabledForm ||
                  (item?.isPackage && item?.amount === 0) ||
                  (item?.isDeposit && item?.amount === 0)
                }
                className={
                  fields.length > 1 && !isParent ? "visible" : "invisible"
                }
              >
                <i className="fas fa-trash" />
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const PaymentSection = ({
  businessId,
  paymentData = {
    method: "",
    cardNumber: "",
    paymentItems: [],
  },
  paymentParentData = [],
  onSubmit = () => undefined,
  itemOptions = [],
  packageOptions = [],
  disabledForm = false,
  loading = false,
  isFieldUser = false,
  // Add these props from parent
  handleAddPaymentItem = () => undefined,
  handleAddPaymentPackage = () => undefined,
  handleDeletePaymentItem = () => undefined,
  handleChangeForm = () => undefined,
  handleUndoItem = () => undefined,
}) => {
  const localize = useLocalization();
  const { role } = useAuth();

  // Check if user is doctor/beautician
  const isDoctor =
    role === roleUser.ROLE_DOCTOR || role === roleUser.ROLE_BEAUTICIAN;

  // Remove internal handlers and use passed ones
  const handleAddItem = () => {
    handleAddPaymentItem();
  };

  const handleAddPackage = () => {
    handleAddPaymentPackage();
  };

  const handleRemoveItem = (index) => {
    handleDeletePaymentItem(index);
  };

  const handleItemChange = (index, field, value) => {
    handleChangeForm("paymentItems", value, field, index);
  };

  // Validation functions
  const requiredArray = (value) =>
    value && value.length > 0 ? undefined : localize.getText("required");

  const validateForm = (values) => {
    const errors = {};

    // Don't validate payment method for doctors
    if (!values.method && !isDoctor) {
      errors.method = localize.getText("pleaseEnterPaymentMethod");
    }

    const isCardNumber =
      values.method === "CREDIT_CARD" || values.method === "DEBIT";

    if (!values.cardNumber && isCardNumber) {
      errors.cardNumber = localize.getText("pleaseEnterCardNumber");
    }

    if (values?.cardNumber?.length < 16 && isCardNumber) {
      errors.cardNumber = localize.getText("pleaseEnter16DigitCardNumber");
    }

    return errors;
  };

  // Calculate discount items for display
  const getDiscountItems = (paymentItems) => {
    return paymentItems
      .filter((item) => item.discount && item.typeDiscount)
      .map((item) => ({
        name: item.isPackage
          ? `${localize.getText("package")} ${item.package?.label || ""}`
          : `${localize.getText("item")} ${item.item?.label || ""}`,
        discount:
          item.typeDiscount === "NUMBER"
            ? formatNumberWithCurrency(-item.discount)
            : `${item.discount}%`,
      }));
  };

  // Calculate total amount for main payment
  const getTotalAmount = (paymentItems) => {
    return (
      paymentItems
        .filter((item) => item.amount !== null)
        .reduce((sum, current) => {
          const amount = Number(current.amount) || 0;
          const discountType = current.typeDiscount;
          const discount = Number(current.discount) || 0;

          let discountedAmount = amount;

          if (discountType === "PERCENTAGE" && discount > 0) {
            discountedAmount -= (amount * discount) / 100;
          } else if (discountType === "NUMBER" && discount > 0) {
            discountedAmount -= discount;
          }

          return sum + Math.max(discountedAmount, 0);
        }, 0) - (Number(paymentData?.saldo) || 0)
    );
  };

  // Calculate total for parent payments (doctor items)
  const getTotalAmountParent = (parentData) => {
    return parentData
      .flatMap((parent) => parent.paymentItems)
      .filter((item) => item.amount !== null)
      .reduce((sum, current) => {
        const amount = Number(current.amount) || 0;
        const discountType = current.typeDiscount;
        const discount = Number(current.discount) || 0;

        let discountedAmount = amount;

        if (discountType === "PERCENTAGE" && discount > 0) {
          discountedAmount -= (amount * discount) / 100;
        } else if (discountType === "NUMBER" && discount > 0) {
          discountedAmount -= discount;
        }

        return sum + Math.max(discountedAmount, 0);
      }, 0);
  };

  const isCardNumber =
    paymentData.method === "CREDIT_CARD" ||
    paymentData.method === "DEBIT" ||
    paymentData.method === "CREDIT_CARD_UOB" ||
    paymentData.method === "CREDIT_CARD_OCBC" ||
    paymentData.method === "CREDIT_CARD_BCA";

  return (
    <Form
      onSubmit={onSubmit}
      mutators={{
        ...arrayMutators,
      }}
      initialValues={paymentData}
      validate={validateForm}
      enableReinitialize={true} // Important: This allows form to update when paymentData changes
    >
      {({ handleSubmit, values }) => (
        <div className={styles.paymentSection}>
          <div className={styles.paymentContent}>
            <div className={styles.paymentHeader}>
              <i className={`far fa-credit-card ${styles.paymentIcon}`} />
              <p className={styles.paymentTitle}>
                {localize.getText("payment")}
              </p>
            </div>

            {/* User fields for pharmacy mode */}
            {isFieldUser && (
              <div className={styles.paymentFields}>
                <Field name="name">
                  {({ input }) => (
                    <InputField
                      label={`${localize.getText("name")} (${localize.getText(
                        "optional"
                      )})`}
                      placeholder={localize.getText("placeholderField", {
                        fieldName: localize.getText("name"),
                      })}
                      fullWidth
                      {...input}
                    />
                  )}
                </Field>
                <Field name="email">
                  {({ input }) => (
                    <InputField
                      label={`${localize.getText("email")} (${localize.getText(
                        "optional"
                      )})`}
                      placeholder={localize.getText("placeholderField", {
                        fieldName: localize.getText("email"),
                      })}
                      fullWidth
                      {...input}
                    />
                  )}
                </Field>
                <Field name="phoneNumber">
                  {({ input }) => (
                    <InputField
                      label={`${localize.getText(
                        "phoneNumber"
                      )} (${localize.getText("optional")})`}
                      placeholder={localize.getText("placeholderField", {
                        fieldName: localize.getText("phoneNumber"),
                      })}
                      fullWidth
                      {...input}
                    />
                  )}
                </Field>
              </div>
            )}

            {/* Payment method fields - not shown for doctors or parent payments */}
            {!isDoctor && (
              <div className={styles.paymentFields}>
                <Field name="method">
                  {({ input, meta }) => (
                    <SelectField
                      label={localize.getText("method")}
                      placeholder={localize.getText("selectPaymentMethod")}
                      data={paymentMethod}
                      labelKey="label"
                      valueKey="value"
                      fullWidth
                      value={paymentData.method}
                      onChange={(value) => handleChangeForm("method", value)}
                      error={meta.touched && meta.error}
                      disabled={disabledForm}
                    />
                  )}
                </Field>

                {isCardNumber && (
                  <Field name="cardNumber">
                    {({ input, meta }) => (
                      <InputField
                        label={localize.getText("cardNumber")}
                        placeholder={localize.getText("enterCardNumber")}
                        fullWidth
                        onChange={(value) =>
                          handleChangeForm("cardNumber", value)
                        }
                        value={paymentData.cardNumber}
                        error={meta.touched && meta.error}
                        disabled={disabledForm}
                      />
                    )}
                  </Field>
                )}
              </div>
            )}

            {/* Main payment items */}
            <FieldArray name="paymentItems" validate={requiredArray}>
              {({ fields }) => (
                <TablePaymentItem
                  fields={fields}
                  paymentData={paymentData}
                  itemOptions={itemOptions}
                  packageOptions={packageOptions}
                  disabledForm={disabledForm}
                  isDoctor={isDoctor}
                  isParent={false}
                  handleChangeItem={(index, field, value) =>
                    handleItemChange(index, field, value)
                  }
                  handleRemoveItem={handleRemoveItem}
                  handleUndoItem={handleUndoItem}
                />
              )}
            </FieldArray>

            {paymentParentData.length > 0 &&
              paymentParentData.map((parentPayment, parentIndex) => (
                <div key={parentIndex} className="mt-4">
                  <p className="font-w500" style={{ fontSize: 14 }}>
                    {localize.getText("doctorItems", {
                      doctorName: parentPayment.doctorName,
                    })}
                  </p>
                  <div className={styles.paymentTable}>
                    <div className={styles.tableHeader}>
                      <p
                        className={`${styles.tableHeaderItem} ${styles.tableHeaderName}`}
                      >
                        {localize.getText("name")}
                      </p>
                      <p
                        className={`${styles.tableHeaderItem} ${styles.tableHeaderQuantity}`}
                      >
                        {localize.getText("quantity")}
                      </p>
                      <p
                        className={`${styles.tableHeaderItem} ${styles.tableHeaderPrice}`}
                      >
                        {localize.getText("price")}
                      </p>
                      <p
                        className={`${styles.tableHeaderItem} ${styles.tableHeaderDiscount}`}
                      >
                        {localize.getText("discountType")}
                      </p>
                      <p
                        className={`${styles.tableHeaderItem} ${styles.tableHeaderDiscountValue}`}
                      >
                        {localize.getText("discount")}
                      </p>
                      <p style={{ width: 48 }}></p>
                    </div>

                    {parentPayment.paymentItems.map((item, itemIndex) => (
                      <div key={itemIndex} className={styles.tableRow}>
                        <InputField
                          value={
                            item.isPackage
                              ? item.package?.label
                              : item.item?.label
                          }
                          disabled
                          size="small"
                          fullWidth
                        />
                        <InputField
                          value={item.qty}
                          disabled
                          size="small"
                          classNameInput={styles.tableFieldQuantity}
                        />
                        <InputField
                          value={formatNumberWithCurrency(item.amount)}
                          disabled
                          size="small"
                          classNameInput={styles.tableFieldPrice}
                        />
                        <InputField
                          value={
                            item.typeDiscount
                              ? typeDiscount.find(
                                  (t) => t.value === item.typeDiscount
                                )?.label
                              : ""
                          }
                          disabled
                          size="small"
                          classNameInput={styles.tableFieldDiscount}
                        />
                        <InputField
                          value={item.discount || 0}
                          disabled
                          size="small"
                          classNameInput={styles.tableFieldDiscountValue}
                        />
                        <p style={{ width: 48 }}></p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>

          <div className={styles.paymentFooter}>
            <div className={styles.paymentActions}>
              <div className={styles.addButtons}>
                <Button
                  leftIcon={<i className="fas fa-plus" />}
                  label={localize.getText("item")}
                  variant="outline-light"
                  className={styles.addButton}
                  onClick={handleAddItem}
                  disabled={disabledForm}
                />
                <Button
                  leftIcon={<i className="fas fa-plus" />}
                  label={localize.getText("package")}
                  variant="outline-light"
                  className={styles.addButton}
                  onClick={handleAddPackage}
                  disabled={disabledForm}
                />
              </div>

              <div className={styles.totalsSection}>
                {/* Show real-time calculations based on paymentData */}
                {getDiscountItems(paymentData.paymentItems || []).map(
                  (item, index) => (
                    <div key={index} className={styles.discountRow}>
                      <p className={styles.discountLabel}>
                        {localize.getText("discountFor", {
                          itemName: item.name.toLowerCase(),
                        })}
                        :
                      </p>
                      <p className={styles.discountValue}>{item.discount}</p>
                    </div>
                  )
                )}
                {paymentData?.saldo > 0 && (
                  <div className={styles.discountRow}>
                    <p className={styles.discountLabel}>
                      {localize.getText("balanceUsed")} :
                    </p>
                    <p className={styles.discountValue}>
                      {formatNumberWithCurrency(paymentData?.saldo || 0)}
                    </p>
                  </div>
                )}

                <div className={styles.totalRow}>
                  <p className={styles.totalLabel}>
                    {localize.getText("total")}:
                  </p>
                  <p className={styles.totalValue}>
                    {formatNumberWithCurrency(
                      getTotalAmount(paymentData.paymentItems || []) +
                        getTotalAmountParent(paymentParentData)
                    )}
                  </p>
                </div>
              </div>
            </div>

            {!disabledForm && (
              <Button
                label={localize.getText("savePayment")}
                fullWidth
                onClick={handleSubmit}
                loading={loading}
              />
            )}
          </div>
        </div>
      )}
    </Form>
  );
};

export default PaymentSection;
